<template>
  <NuxtLayout name="standard">
    <template v-if="strapiPageData">
      <ConnectifTag :name="CONNECTIF_PAGE_TAGS.OTHERS" />
      <ConnectifTag :name="CONNECTIF_CONTENT_TAGS.OTHERS" />

      <SHeroSearch
        v-if="strapiPageData.attributes.hero_search"
        v-bind="heroSearch"
      />
      <SSection
        v-if="strapiPageData.attributes.hero_image"
        :has-separator="false"
        :mobile-full="true"
      >
        <SHeroCard v-bind="heroCard"></SHeroCard>
      </SSection>
      <SSection
        v-if="strapiPageData.attributes.simple_banner"
        :has-separator="false"
      >
        <SSimpleBanner
          :text="strapiPageData.attributes.simple_banner.text"
          :button="strapiPageData.attributes.simple_banner.button"
        ></SSimpleBanner>
      </SSection>
      <SShortDescriptionWrapper
        v-if="strapiPageData.attributes.intro_seo_text"
        v-bind="introSeoTexts"
      />
      <SCtaCardWrapper v-if="imageBanners" v-bind="imageBanners" />
      <SCardsImagesWrapper
        v-if="strapiPageData.attributes.featured_destinations"
        v-bind="featuredDestinations"
      />
      <SMediaBannerWrapper v-if="mediaBanner" v-bind="mediaBanner" />
      <SCardsTinyImagesWrapper
        v-if="featuredSearches"
        v-bind="featuredSearches"
      />
      <SCardsRatedWrapper :items="featuredCottages" />
      <SSectionColor
        v-if="strapiPageData.attributes.featured_posts"
        :title="strapiPageData.attributes.featured_posts.title || undefined"
        background-color="gray-50"
      >
        <SBlogposts v-bind="dataPosts" />
      </SSectionColor>
      <SLinksWrapper
        v-if="
          strapiPageData.attributes.seo_links &&
          strapiPageData.attributes.seo_links.length > 0
        "
        :items="seoLinks.items"
      />
      <SSpacer />
    </template>
  </NuxtLayout>
</template>

<script setup lang="ts">
// Constants
import {
  CONNECTIF_PAGE_TAGS,
  CONNECTIF_CONTENT_TAGS,
} from '~~/modules/integrations/connectif/constants'
import { sendConnectifPage } from '~~/modules/integrations/connectif/composables/useConnectifComposable'

// Types
import { type schemaType as escapadasDetailSchematype } from '~~/modules/api/strapi/escapadas/detail/schema'

// Composables
import { useHeadInfoComposable } from '~~/modules/head/composables/useHeadInfoComposable'
import { useMenuBreadcrumbStore } from '~~/modules/menu/stores/useMenuBreadcrumbStore'

// Transformers
import { useToSMediaBannerWrapper } from '~/composables/transformers/useToSMediaBannerWrapper'
import { useToSCardsImagesWrapper } from '~/composables/transformers/useToSCardsImagesWrapper'
import { useToSCardsTinyImagesWrapper } from '~/composables/transformers/useToSCardsTinyImagesWrapper'
import { useToSBlogposts } from '~/composables/transformers/useToSBlogposts'
import { useToSCtaCardWrapper } from '~/composables/transformers/useToSCtaCardWrapper'
import { useToSLinksWrapper } from '~/composables/transformers/useToSLinksWrapper'
import { useToSCardsRatedWrapper } from '~/composables/transformers/useToSCardsRatedWrapper'
import { useToSShortDescriptionWrapper } from '~/composables/transformers/useToSShortDescriptionWrapper'

// Components Props
import type { propsInterface as sMediaBannerWrapperProps } from '~~/modules/design-system/sections/s-media-banner/s-media-banner-wrapper.vue'
import type { propsInterface as sCardsImagesWrapperProps } from '~~/modules/design-system/sections/s-cards-images/s-cards-images-wrapper.vue'
import type { propsInterface as sCardsTinyImagesWrapperProps } from '~~/modules/design-system/sections/s-cards-tiny-images/s-cards-tiny-images-wrapper.vue'
import type { propsInterface as sBlogPostsProps } from '~~/modules/design-system/sections/s-blogposts.vue'
import type { propsInterface as sCtaCardWrapperProps } from '~~/modules/design-system/sections/s-cta-card/s-cta-card-wrapper.vue'
import type { propsInterface as sCardsRatedWrapperProps } from '~~/modules/design-system/sections/s-cards-rated/s-cards-rated-wrapper.vue'
import type { propsInterface as sLinksWrapperProps } from '~~/modules/design-system/sections/s-links/s-links-wrapper.vue'
import type { propsInterface as sHeroSearchProps } from '~~/modules/design-system/sections/s-hero-search.vue'
import type { propsInterface as sHeroCardProps } from '~~/modules/design-system/sections/s-hero-card.vue'
import type { propsInterface as sShortDescriptionWrapperProps } from '~~/modules/design-system/sections/s-short-description/s-short-description-wrapper.vue'

// Apis
import { serverFetch as getPageData } from '~~/modules/api/strapi/escapadas/detail'
import { serverFetch as getBlogPosts } from '~~/modules/api/rurality/v3/blog/posts/list'
import { serverFetch as getCottagesList } from '~~/modules/api/rurality/v3/cottages/list'

// Composables
const route = useRoute()
const { setHeadInfo } = useHeadInfoComposable()

// Methods
const showErrorPage = (error: ApiResponseError) => {
  throw createError({
    statusCode: 503,
    message: error.message,
    fatal: true,
  })
}

const showErrorNotFound = () => {
  throw createError({
    statusCode: 404,
    fatal: true,
  })
}

let strapiPageData: escapadasDetailSchematype
let imageBanners: sCtaCardWrapperProps | null
let featuredDestinations: sCardsImagesWrapperProps
let mediaBanner: sMediaBannerWrapperProps | null
let featuredSearches: sCardsTinyImagesWrapperProps | null
const featuredCottages: sCardsRatedWrapperProps['items'] = []
let dataPosts: sBlogPostsProps
let seoLinks: sLinksWrapperProps
let heroSearch: sHeroSearchProps
let heroCard: sHeroCardProps
let introSeoTexts: sShortDescriptionWrapperProps

const rawStrapiPageData = await getPageData({
  slug: route.params.slug as string,
})

if (
  rawStrapiPageData.success &&
  rawStrapiPageData.success.data.attributes.publishedAt
) {
  strapiPageData = rawStrapiPageData.success.data

  if (strapiPageData) {
    setHeadInfo({
      title: strapiPageData.attributes.metadata.title,
      description: strapiPageData.attributes.metadata.description,
      images: [
        {
          url: strapiPageData.attributes.metadata.image.data.attributes.url,
          alt: '',
        },
      ],
      robotsVisible: true,
    })

    if (strapiPageData.attributes.hero_search) {
      heroSearch = {
        image: strapiPageData.attributes.hero_search.image.data.attributes.url,
        title: strapiPageData.attributes.title,
        badge: strapiPageData.attributes.hero_search.author,
        provinceFilter:
          strapiPageData.attributes.hero_search.search.filter_type ===
          'provincia'
            ? strapiPageData.attributes.hero_search.search.filter_value
            : undefined,
        regionFilter:
          strapiPageData.attributes.hero_search.search.filter_type ===
          'comunidad-autonoma'
            ? strapiPageData.attributes.hero_search.search.filter_value
            : undefined,
      }
    }

    if (strapiPageData.attributes.hero_image) {
      heroCard = {
        image: strapiPageData.attributes.hero_image.image.data.attributes.url,
        title: strapiPageData.attributes.title,
      }
    }

    if (strapiPageData.attributes.image_banner) {
      imageBanners =
        useToSCtaCardWrapper().fromStrapiEscapadasDetail(strapiPageData)
    }

    if (strapiPageData.attributes.intro_seo_text) {
      introSeoTexts = useToSShortDescriptionWrapper().fromStrapiEscapadasDetail(
        {
          dataIn: strapiPageData,
        },
      )
    }

    if (strapiPageData.attributes.featured_destinations) {
      featuredDestinations =
        useToSCardsImagesWrapper().fromStrapiEscapadasDetail(strapiPageData)
    }

    if (strapiPageData.attributes.media_banner) {
      mediaBanner =
        useToSMediaBannerWrapper().fromStrapiEscapadasDetail(strapiPageData)
    }

    if (strapiPageData.attributes.featured_searches) {
      featuredSearches =
        useToSCardsTinyImagesWrapper().fromStrapiEscapadasDetail(strapiPageData)
    }

    if (strapiPageData.attributes.featured_posts) {
      const getPosts = await getBlogPosts({
        slugs: strapiPageData.attributes.featured_posts.slugs,
      })
      if (getPosts.success && getPosts.success.data) {
        dataPosts = useToSBlogposts().fromBlogPostList(getPosts.success.data)
        if (strapiPageData.attributes.featured_posts.button) {
          dataPosts.button = strapiPageData.attributes.featured_posts.button
        }
      }
    }

    if (strapiPageData.attributes.featured_cottages) {
      for (
        let i = 0;
        i < strapiPageData.attributes.featured_cottages.length;
        i++
      ) {
        const cottage = strapiPageData.attributes.featured_cottages[i]
        const facets = cottage.filters

        const dataGetCottages = await getCottagesList({ facets })
        if (dataGetCottages?.success) {
          const featuredCottage:
            | sCardsRatedWrapperProps['items'][number]
            | null = useToSCardsRatedWrapper().fromStrapiEscapadasDetail(
            cottage,
            dataGetCottages.success.data,
          )
          if (featuredCottage !== null) {
            featuredCottages.push(featuredCottage)
          }
        }
      }
    }
    if (strapiPageData.attributes.seo_links) {
      seoLinks = useToSLinksWrapper().fromStrapiEscapadasDetail({
        dataIn: strapiPageData,
      })
    }
    const breadCrumbMenuStore = useMenuBreadcrumbStore()
    breadCrumbMenuStore.setMenuBreadcrumb(strapiPageData.attributes.title, [
      {
        href: routes().escapadasList(),
        name: t('menu.breadcrumb.escapadas'),
      },
    ])
  }
} else if (
  rawStrapiPageData.success &&
  !rawStrapiPageData.success.data.attributes.publishedAt
) {
  showErrorNotFound()
} else if (rawStrapiPageData.error) {
  showErrorPage(rawStrapiPageData.error as ApiResponseError)
}

sendConnectifPage()
</script>
